import {
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  Renderer2,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';
import { UpdateWidgetComponent } from './_components/update-widget/update-widget.component';
import { EventShareService } from './_services/event-share.service';

const WIDGET_CSS_CLASS = 'my-dynamic-class';
const WIDGET_X_OFFSET = 280;
const WIDGET_Y_OFFSET = 20;
const SIDEBAR_WIDTH_THRESHOLD = 339;
const VIEWPORT_EDGE_PADDING = 10;
const OVERLAY_PANE_Z_INDEX = '1001';

@Directive({
  selector: '[appRelativeClickPosition]',
})
export class RelativeClickPositionDirective {
  private componentRef: ComponentRef<UpdateWidgetComponent>;

  @Input() appRelativeClickPosition!: any;

  constructor(
    private elementRef: ElementRef,
    private rootViewContainer: ViewContainerRef,
    private renderer: Renderer2,
    private eventShareService: EventShareService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['appRelativeClickPosition'].currentValue) return;
    this.renderer.addClass(this.elementRef.nativeElement, 'widget-click');
  }

  @HostListener('click', ['$event'])
  onClickInside(event: any): void {
    event.stopPropagation();
    this.destroyWidget();

    if (event.target.classList.contains('widget-click')) {
      this.openWidget(event.target);
    }
  }

  private openWidget(triggerEl: HTMLElement): void {
    const triggerRect = triggerEl.getBoundingClientRect();
    const { scrollLeft: scrollX, scrollTop: scrollY }: any =
      document.documentElement || document.body.parentNode || document.body;
    const scroll = { x: scrollX, y: scrollY };

    this.componentRef = this.rootViewContainer.createComponent(UpdateWidgetComponent);
    this.mountWidgetInDOM();
    this.setupWidgetInstance();

    const position = this.calculatePosition(triggerRect, scroll);
    this.componentRef.instance.positionRelativeTo(position.x, position.y);
    this.attachOutsideClickGuard();
  }

  private mountWidgetInDOM(): void {
    const widgetEl = this.componentRef.location.nativeElement;
    const overlayPanes = document.querySelectorAll('.cdk-global-overlay-wrapper');
    const lastPane = overlayPanes[overlayPanes.length - 1];

    if (lastPane) {
      lastPane.querySelector('.cdk-overlay-pane').prepend(widgetEl);
    } else {
      document.body.appendChild(widgetEl);
    }

    this.renderer.addClass(widgetEl, WIDGET_CSS_CLASS);
    this.renderer.setStyle(widgetEl, 'position', 'absolute');

    if (lastPane) {
      this.renderer.setStyle(widgetEl, 'z-index', OVERLAY_PANE_Z_INDEX);
    } else {
      this.renderer.removeStyle(widgetEl, 'z-index');
    }
  }

  private setupWidgetInstance(): void {
    const instance = this.componentRef.instance;

    instance.widgetInfo = this.appRelativeClickPosition;

    instance.closeModal.subscribe((res) => {
      const currentOpenWidget = this.eventShareService.getCurrentOpenWidget();
      if (currentOpenWidget === res?.widgetInfo?.widgetName) {
        this.destroyWidget();
      }
    });

    this.componentRef.changeDetectorRef.detectChanges();

    instance.output.subscribe((data) => {
      this.eventShareService.setWidgetResponseData({ data, componentRef: this.componentRef });
    });
  }
  private attachOutsideClickGuard(): void {
    // Inject a style tag to override cursor on ALL elements outside the widget.
    // Setting cursor on body alone is not enough — button/link rules have higher
    // specificity and would still show pointer. !important wins over all of them.
    this.injectCursorOverrideStyle();

    const handler = (event: MouseEvent) => {
      const widgetEl = document.querySelector(`.${WIDGET_CSS_CLASS}`);
      const clickedInsideWidget = widgetEl?.contains(event.target as Node);

      if (!clickedInsideWidget) {
        event.stopPropagation();
        event.preventDefault();
        this.destroyWidget();
      }
    };

    // Defer by one tick so this handler doesn't catch the opening click itself
    setTimeout(() => {
      document.addEventListener('click', handler, { capture: true, once: true });
    });
  }

  private injectCursorOverrideStyle(): void {
    const style = this.renderer.createElement('style') as HTMLStyleElement;
    style.id = 'widget-cursor-override';
    style.textContent = `body *:not(.${WIDGET_CSS_CLASS}):not(.${WIDGET_CSS_CLASS} *) { cursor: default !important; }`;
    document.head.appendChild(style);
  }

  private restoreOutsideCursor(): void {
    document.getElementById('widget-cursor-override')?.remove();
  }
  private calculatePosition(
    triggerRect: DOMRect,
    scroll: { x: number; y: number },
  ): { x: number; y: number } {
    const widgetEl = document.querySelector<HTMLElement>(`.${WIDGET_CSS_CLASS}`);
    const widgetHeight = widgetEl?.offsetHeight || 0;
    const widgetWidth = widgetEl?.offsetWidth || 0;

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Default position: below and left-aligned to the trigger
    let posX = triggerRect.left + scroll.x - WIDGET_X_OFFSET;
    let posY = triggerRect.top + scroll.y + WIDGET_Y_OFFSET;

    // Flip above if widget would overflow the bottom of the viewport
    const wouldOverflowBottom =
      triggerRect.bottom + WIDGET_Y_OFFSET + widgetHeight > viewportHeight;
    if (wouldOverflowBottom) {
      const posYAbove = triggerRect.top + scroll.y - widgetHeight;
      posY = posYAbove >= 0 ? posYAbove : Math.max(scroll.y, posY);
    }

    // Shift left if widget would overflow the right edge of the viewport
    const rightEdge = posX + widgetWidth;
    const viewportRightEdge = viewportWidth + scroll.x;
    if (rightEdge > viewportRightEdge) {
      posX = viewportRightEdge - widgetWidth - VIEWPORT_EDGE_PADDING;
    } else {
      // Compensate for sidebar offset when trigger is near the left edge
      const triggerLeft = this.elementRef.nativeElement.getBoundingClientRect().left;
      if (triggerLeft < SIDEBAR_WIDTH_THRESHOLD) {
        posX += triggerLeft;
      }
    }

    return { x: posX, y: posY };
  }

  private destroyWidget(): void {
    this.restoreOutsideCursor();
    this.componentRef?.destroy();
    this.removeDynamicComponentFromDOM();
  }

  private removeDynamicComponentFromDOM(): void {
    document.querySelectorAll(`.${WIDGET_CSS_CLASS}`).forEach((el) => el.remove());
  }
}
